/**
 * The public site's navigation, in one place.
 *
 * Every marketing page used to declare its own header and footer inline. That
 * is how a single misspelled contact address ("weavin.ai") came to live in
 * seven files at once, and why a page reached from a search result offered no
 * way into the rest of the site — only "Back to Home".
 *
 * Order is the nav order and it is deliberate: what the product is, then the
 * three ways in the seed deck names (organizations that deliver care, funders
 * that pay for it, partners that build on it), then the proof. Employers are a
 * different buyer, so they sit at the end of the Solutions dropdown rather than
 * among the three ways in (they had their own top-level menu until 2026-11-02).
 */
export interface NavLink {
  to: string;
  label: string;
  /** Rendered in the red emphasis style. */
  urgent?: boolean;
}

export interface NavGroup {
  label: string;
  links: NavLink[];
  /** Align the dropdown panel to the right edge (for the last nav item). */
  alignRight?: boolean;
}

/** The leading nav item: what the product is, before who it is for. */
export const PLATFORM: NavLink = { to: '/platform', label: 'Platform' };

export const SOLUTIONS: NavGroup = {
  label: 'Solutions',
  links: [
    { to: '/solutions/hubs', label: 'For Community Organizations & Hubs' },
    { to: '/solutions/health-plans', label: 'For Health Plans & Counties' },
    { to: '/partners', label: 'For Partners & Developers' },
    { to: '/solutions/providers', label: 'For Hospitals & Health Systems' },
    { to: '/solutions/emergency-response', label: 'Emergency Response' },
    // Employers sit under Solutions (decision 2026-11-02), last: a different
    // buyer, so they follow the three ways in rather than lead.
    { to: '/solutions/employers', label: 'For Employers: Employee Health & SDOH' },
    { to: '/solutions/workforce-management', label: 'Workforce Management' },
    { to: '/roi-calculator', label: 'ROI Calculator' },
  ],
};

/** Top-level links between the two dropdowns. */
export const TOP_LINKS: NavLink[] = [
  { to: '/funder-coverage', label: 'Funder Coverage' },
  { to: '/impact', label: 'Impact' },
  { to: '/developers', label: 'Developers' },
  { to: '/company/about', label: 'Company' },
];

/** Flat list used by the mobile strip, where dropdowns are not available. */
export const MOBILE_LINKS: NavLink[] = [
  { to: '/platform', label: 'Platform' },
  { to: '/solutions/hubs', label: 'Community Orgs' },
  { to: '/solutions/health-plans', label: 'Health Plans' },
  { to: '/partners', label: 'Partners' },
  { to: '/funder-coverage', label: 'Funder Coverage' },
  { to: '/impact', label: 'Impact' },
  { to: '/developers', label: 'Developers' },
  { to: '/solutions/providers', label: 'Hospitals' },
  { to: '/solutions/employers', label: 'For Employers' },
  { to: '/company/about', label: 'Company' },
  { to: '/contact', label: 'Contact Us' },
];

export const FOOTER_SOLUTIONS: NavLink[] = [
  { to: '/solutions/hubs', label: 'For Community Organizations & Hubs' },
  { to: '/solutions/health-plans', label: 'For Health Plans & Counties' },
  { to: '/partners', label: 'For Partners & Developers' },
  { to: '/solutions/providers', label: 'For Hospitals & Health Systems' },
  { to: '/solutions/emergency-response', label: 'Emergency Response' },
  { to: '/solutions/employers', label: 'For Employers' },
  { to: '/solutions/workforce-management', label: 'Workforce Management' },
  { to: '/roi-calculator', label: 'ROI Calculator' },
];

export const FOOTER_COMPANY: NavLink[] = [
  { to: '/platform', label: 'Platform' },
  { to: '/company/about', label: 'About Us' },
  { to: '/company/team', label: 'Our Team' },
  { to: '/funder-coverage', label: 'Funder Coverage' },
  { to: '/impact', label: 'Impact' },
  { to: '/resources', label: 'Resources' },
  { to: '/developers', label: 'Developers' },
  { to: '/security', label: 'Security' },
  { to: '/contact', label: 'Contact Us' },
];

export const FOOTER_LEGAL: NavLink[] = [
  { to: '/consent/terms-of-use', label: 'Terms of Use' },
  // The static file, not the /consent/privacy-policy route: Google's OAuth
  // verification crawler does not run JavaScript, so it only sees this one.
  { to: '/privacy-policy.html', label: 'Privacy Policy' },
  { to: '/consent/hipaa-authorization', label: 'HIPAA Authorization' },
];

/**
 * The legacy Trust application. It stays reachable from the public site for as
 * long as accounts live on it: people who have only ever signed in at
 * trust.care need somewhere obvious to go once trust.care itself points here.
 */
export const LEGACY_APP_URL = 'https://app.trust.care/login';

/**
 * Label for the legacy Trust application login. Kept here so the header
 * dropdown and the footer cannot drift apart. People who have only ever signed
 * in at trust.care recognise "Trust.Care" rather than the internal word
 * "legacy", so that is what the public site shows them; the app.trust.care
 * subtitle still spells out where the link goes.
 */
export const LEGACY_LOGIN_LABEL = 'Trust.Care Login';

export const CONTACT_EMAIL = 'welcome@weavein.ai';
export const SUPPORT_EMAIL = 'support@weavein.ai';

/**
 * The member door (TRUS-778). weavein.ai is written for organizations, but
 * people looking for help for themselves land here too; these are their way
 * in. The help line itself lives in lib/memberHelpLine.ts (useMemberHelpLine) so
 * the marketing site and the member pages cannot drift apart.
 */
export const MEMBER_DOOR_URL = '/help';
export const MEMBER_SIGN_IN_URL = '/member/login';
